import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Calendar, Loader2, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log('Login attempt started for:', email);

    try {
      console.log('Calling login API...');
      const result = await login(email, password);
      console.log('Login successful, user:', result);
      toast.success('Welcome back!');
      console.log('Navigating to dashboard...');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative bg-cover bg-center"
      style={{ backgroundImage: "url('/login-bg.jpg')" }}
    >
      {/* Dark transparency mask */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-hidden="true" />

      <div className="w-full max-w-lg animate-fade-in relative z-10">
        <div className="flex flex-col items-center mb-8">
          <img 
            src="/logo.jpg" 
            alt="API Job Board Logo" 
            className="w-56 h-auto mb-4 rounded-lg shadow-md"
          />
          <h1 className="font-heading text-4xl font-bold">Job Board</h1>
          <p className="text-muted-foreground mt-1 text-lg">Personnel Scheduling Made Simple</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-heading text-3xl">Welcome back</CardTitle>
            <CardDescription className="text-base">Sign in to manage your schedule</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-primary"
                >
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                className="w-full btn-pill"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Do not have an account? </span>
              <Link
                to="/register"
                className="text-primary hover:underline font-medium"
                data-testid="register-link"
              >
                Create one
              </Link>
            </div>
            <div className="mt-3 text-center flex justify-center gap-3">
              <Link
                to="/privacy"
                className="text-xs text-muted-foreground hover:text-primary"
                data-testid="privacy-policy-link"
              >
                Privacy Policy
              </Link>
              <span className="text-xs text-muted-foreground">&middot;</span>
              <Link
                to="/sms-terms"
                className="text-xs text-muted-foreground hover:text-primary"
                data-testid="sms-terms-link"
              >
                SMS Terms
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
